/**
 * Uma célula com foto enviada: a imagem, a legenda e (nas de registro) o selo "adotada". Legenda e selo gravam
 * por PUT ao sair do campo ou ao alternar; se o servidor recusar, o texto digitado fica e o erro aparece aqui.
 */
import React, { useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { ergonomicWorkstationService } from '../../../../../services/ergonomicWorkstationService';
import type { ErgonomicPhoto } from '../../../../../types/ergonomicWorkstation';
import { errorMessage } from '../wizardValidation';
import { PhotoImage } from './PhotoImage';

interface PhotoSlotProps {
  photo: ErgonomicPhoto;
  /** Posição na seção, para nomear a foto ("Foto 3"). */
  number: number;
  canEdit: boolean;
  disabled: boolean;
  onChanged: (photo: ErgonomicPhoto) => void;
  onRemove: (photo: ErgonomicPhoto) => void;
  onSaved: (message: string) => void;
}

export const PhotoSlot: React.FC<PhotoSlotProps> = ({ photo, number, canEdit, disabled, onChanged, onRemove, onSaved }) => {
  const [caption, setCaption] = useState(photo.caption ?? '');
  const [error, setError] = useState<string | null>(null);
  // Só o selo trava enquanto grava; a legenda grava ao sair do campo e não pode impedir o clique em "Remover".
  const [savingAdopted, setSavingAdopted] = useState(false);
  const name = photo.kind === 'ABERTURA' ? 'Foto de abertura' : `Foto ${number}`;

  const save = async (patch: { caption?: string | null; adopted?: boolean }, message: string) => {
    const isAdopted = patch.adopted !== undefined;
    if (isAdopted) setSavingAdopted(true);
    setError(null);
    try {
      const updated = await ergonomicWorkstationService.updatePhoto(photo.workstationId, photo.id, patch);
      onChanged(updated);
      onSaved(message);
    } catch (err: unknown) {
      setError(errorMessage(err, 'Não foi possível guardar. O que você digitou continua aqui; tente de novo.'));
    } finally {
      if (isAdopted) setSavingAdopted(false);
    }
  };

  const saveCaption = () => {
    const trimmed = caption.trim();
    if (trimmed === (photo.caption ?? '')) return;
    save({ caption: trimmed === '' ? null : trimmed }, 'Legenda guardada.');
  };

  return (
    <Box component="li" sx={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1 }}>
      <PhotoImage workstationId={photo.workstationId} photoId={photo.id} alt={photo.caption || name} />
      <TextField
        fullWidth
        multiline
        minRows={2}
        label={`Legenda — ${name}`}
        value={caption}
        onChange={(event) => setCaption(event.target.value)}
        onBlur={saveCaption}
        disabled={disabled}
        slotProps={{ htmlInput: { readOnly: !canEdit } }}
      />
      {photo.kind === 'REGISTRO' && (
        <FormControlLabel
          control={
            <Checkbox
              checked={photo.adopted}
              disabled={!canEdit || disabled || savingAdopted}
              onChange={(event) =>
                save(
                  { adopted: event.target.checked },
                  event.target.checked ? 'Foto marcada como adotada.' : 'Foto desmarcada como adotada.'
                )
              }
            />
          }
          label={photo.adopted ? 'Adotada ✔' : 'Adotada'}
          sx={{ minHeight: 44, '& .MuiFormControlLabel-label': { fontSize: '1rem' } }}
        />
      )}
      {error && <Alert severity="error" sx={{ fontSize: '1rem' }}>{error}</Alert>}
      {canEdit && (
        <Button
          variant="text"
          color="error"
          disabled={disabled}
          onClick={() => onRemove(photo)}
          sx={{ alignSelf: 'flex-start', minHeight: 44 }}
        >
          {`Remover ${name.toLowerCase()}`}
        </Button>
      )}
    </Box>
  );
};
